import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';

import * as constants from '../../common/constants';
import * as Enums from '../../models/enum';
import { GroupDto } from '../../models/DTO/GroupDto';
import { IRequestService } from '../request.service';
import { IDateRangeService } from '../general/dateRange.service';
import { ILoggerService } from '../general/logger.service';
import { IIdentifierVmService } from '../vm/identifierVm.service';
import { IwidgetIntervalHelperService } from '../helpers/widgetIntervalHelper.service';
import { ICustomPropertiesService } from '.';
import { IGroupTreeHelperService, IUploadService } from '../helpers';
import { partition } from '../../common';
import {
    CreateGroupFromGroupDto,
    Dictionary,
    Group,
    GroupFromTags,
    IGroupConfiguration,
    IValueViewModel,
    Identifier,
    ImportResult,
    ImportResultDto,
    ObjectUser,
    Range,
    ReconciliationResults,
    ScriptType,
    ExtendedState,
    TreeType
} from '../../models';

export interface IGroupService {
    getAll(): angular.IPromise<GroupDto[]>;
    getAll(force: boolean): angular.IPromise<GroupDto[]>;
    getByGuid(guid: string): angular.IPromise<GroupDto>;
    addGroup(group: any): angular.IPromise<GroupDto[]>;
    deleteGroup(guid: string): angular.IPromise<GroupDto[]>;
    updateGroup(group: Group): angular.IPromise<GroupDto[]>;
    getForTag(guid: string): angular.IPromise<GroupDto[]>;
    getForState(guid: string): angular.IPromise<GroupDto[]>;
    getForTypes(types: string[]): angular.IPromise<Group[]>;
    getAllAsTree(): angular.IPromise<TreeType[]>;
    getAllAsTreeOrdered(): angular.IPromise<Group[]>;
    getTagMinMax(guid: string, vm: IValueViewModel, groups: Dictionary<Group>): angular.IPromise<{ min: number; max: number }>;
    getIdentifierMinMax(guid: string): angular.IPromise<any>;
    getIdentifierGlobalMinMax(guid: string): angular.IPromise<Range>;
    getIdentifier(guid: string): angular.IPromise<any>;
    getIdentifiers(guidList: string[]): angular.IPromise<Identifier[]>;
    getConfigurationForGroup(guid: string): angular.IPromise<IGroupConfiguration>;
    getReferenceStates(guid: string, force?: boolean): angular.IPromise<ExtendedState[]>;

    testScript(
        groupGuid: string,
        script: string,
        date: string,
        scriptTestMode: Enums.ScriptTestMode,
        libraryCollection: string[]
    ): angular.IPromise<any>;
    updateOccurenceMatrix(
        groupGuid: string,
        script: string,
        date: string,
        libraryCollection: string[],
        testMatrix: boolean
    ): angular.IPromise<any>;
    updateAllOccurenceMatrix(): angular.IPromise<{ [name: string]: string }>;
    updateSomeOccurenceMatrix(guidList: string[]): angular.IPromise<{ [name: string]: string }>;

    getActualStates(guid: string, force?: boolean): angular.IPromise<any>;
    updateConfigurationForGroup(data: IGroupConfiguration): angular.IPromise<IGroupConfiguration>;

    addIdentifier(guid: string, identifier: Identifier): angular.IPromise<Identifier>;
    createGroupFromTags(postObject: GroupFromTags): angular.IPromise<GroupDto>;
    duplicateIdentifier(groupGuid: string, identifierGuid: string, newIdentifierName: string, newTagDefinitionGuid: string): angular.IPromise<any>;
    reorderIdentifiers(groupGuid: string, identifiersOrder: string[]): angular.IPromise<Identifier[]>;
    updateIdentifiers(guid, identifiers: Identifier[]): angular.IPromise<Identifier[]>;
    updateLibraries(guid: string, libraries: string[], script: string, type: ScriptType): angular.IPromise<any>;
    updateScript(guid: string, script: any): angular.IPromise<any>;

    createFrom(data: CreateGroupFromGroupDto): angular.IPromise<GroupDto>;
    deleteIdentifier(groupGuid: string, identifierGuid: string): angular.IPromise<number>;
    isChildOrSubChild(guid: string, parentGuid: string): angular.IPromise<boolean>;

    autofit(group: Group): angular.IPromise<any>;
    fitone(groupGuid: string, identifierGuid: string): angular.IPromise<any>;
    reconcile(groupGuid: string, dateTime: string): angular.IPromise<ReconciliationResults>;

    getDependencyTree(guid: string): angular.IPromise<any>;
    getPaths(guidList: string[]): angular.IPromise<{ guid: string, path: string }[]>;
    getPrecedencyTree(guid: string): angular.IPromise<any>;

    convertToTree(groups: Group[]): TreeType[];
    getGroupsUsers(): angular.IPromise<any[]>;

    aggregateGuidForGroup(groups: Group[]): angular.IPromise<{ [guid: string]: string[]; }>;
    canDeleteGroup(guid: string, usages: ObjectUser[], groups: Dictionary<Group>): boolean;
    cleanGroupCache(): void;
    getAggregateKey(guid: string, index: { [guid: string]: string[]; }): string;
    getClosestByType(groupGuid: string, types: string[]): angular.IPromise<string>;
    getSteps(): angular.IPromise<{ [groupGuid: string]: { [index: number]: boolean } }>;
    getUsagesForAllGroups(): angular.IPromise<{ [guid: string]: boolean }>;
    getUsagesForGroup(guid: string): angular.IPromise<ObjectUser[]>;
    upload(file: File): angular.IPromise<ImportResult>;
}

export class GroupService implements IGroupService {
    static serviceId = 'groupService';

    static $inject = [
        '$q',
        '$state',
        'dateRangeService',
        'widgetIntervalHelperService',
        'loggerService',
        'requestService',
        'identifierVmService',
        'customPropertiesService',
        'groupTreeHelperService',
        'uploadService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$state: angular.ui.IStateService,
        private _dateRangeService: IDateRangeService,
        private _widgetIntervalHelperService: IwidgetIntervalHelperService,
        private _loggerService: ILoggerService,
        private _requestService: IRequestService,
        private _identifierVmService: IIdentifierVmService,
        private _customPropertiesService: ICustomPropertiesService,
        private _groupTreeHelperService: IGroupTreeHelperService,
        private _uploadService: IUploadService
    ) {
    }

    private controllerUrl(): string {
        const url = this._requestService.getUrlV1() + 'group/';
        return url;
    }

    private getIdentifierServiceUrl(): string {
        return this._requestService.getUrlV1() + 'identifier/';
    }

    getAll(force?: boolean): angular.IPromise<GroupDto[]> {
        if (force) {
            this._requestService.cleanCache(this.controllerUrl());
        }
        return this._requestService.get(this.controllerUrl(), undefined, true);
    }

    getByGuid(guid: string): angular.IPromise<GroupDto> {
        const url = this._requestService.getUrlV1() + 'group/' + guid + '/';
        return this._requestService.get(url, undefined, true);
    }

    deleteGroup(guid: string): angular.IPromise<GroupDto[]> {
        const url = this._requestService.getUrlV1() + 'group/delete/' + guid + '/';
        return this._requestService
            .post(url)
            .then(data => {
                this._requestService.cleanCache(url);
                this.cleanCacheTemplate();
                this._requestService.cleanCacheAtV1('tagDefinition/');
                this._requestService.cleanCacheAtV1('tagDefinition/getList/');
                this._requestService.cleanCacheAtV1('validationPageConfiguration/validableGroups/');
                this._requestService.cleanCacheAtV1('approvalStrategy/getGroups/');
                this._requestService.cleanCacheAtV1('group/steps/');
                return data;
            });
    }

    updateGroup(group: Group): angular.IPromise<GroupDto[]> {
        const url = this._requestService.getUrlV1() + 'group/update/';
        this._requestService.cleanCache(this._requestService.getUrlV1() + 'group/');
        this._requestService.cleanCache(this._requestService.getUrlV1() + 'group/' + group.guid + '/');
        return this._requestService.post(url, group, undefined, true)
            .then(data => {
                //#27702: Clean all cache 'cos it's simplier
                this._requestService.cleanAllCache();
                return data;
            });
    }

    addGroup(group: Group): angular.IPromise<GroupDto[]> {
        const url = this._requestService.getUrlV1() + 'group/add/';

        return this._requestService.post(url, group, undefined, true)
            .then(data => {
                this._requestService.cleanCache(this._requestService.getUrlV1() + 'group/');
                this._requestService.cleanCacheAtV1('template/changes/all/');
                this._requestService.cleanCacheAtV1('validationPageConfiguration/validableGroups/');
                this._requestService.cleanCacheAtV1('approvalStrategy/getGroups/');
                this._requestService.cleanCacheAtV1('group/steps/');
                return data;
            });
    }

    getForTag(guid: string): angular.IPromise<GroupDto[]> {
        const url = this._requestService.getUrlV1() + 'group/tag/' + guid + '/';
        return this._requestService.get(url);
    }

    getForState(guid: string): angular.IPromise<GroupDto[]> {
        const url = this._requestService.getUrlV1() + 'group/state/' + guid + '/';
        return this._requestService.get(url);
    }

    getTagMinMax(guid: string, vm: IValueViewModel, groups: Dictionary<Group>): angular.IPromise<{ min: number; max: number }> {
        let range: any;
        const result = {
            min: 0,
            max: 1
        };

        vm.groupList
            .map(g => groups[g.guid])
            .forEach(group => {
                const identifier = _.find(group.identifierCollection, i => i.tagDefinitionGuid === guid);
                if (!range) {
                    group.groupStateCollection.forEach(gs => {
                        if (!range) {
                            range = _.find(gs.rangeConditionCollection, rc => rc.valueType === 0 && rc.identifierGuid === identifier.guid);
                            if (range) {
                                this._loggerService.log(`Range found in group ${group.name}`, false);
                            }
                        }
                    });
                }
            });

        if (range) {
            result.min = range.min;
            result.max = range.max;
        }
        return this._$q.when(result);
    }

    getIdentifierMinMax(guid): angular.IPromise<any> {
        const url = this.getIdentifierServiceUrl() + 'getMinMax/' + guid + '/';
        return this._requestService.get(url);
    }

    getIdentifierGlobalMinMax(guid): angular.IPromise<Range> {
        const url = this.getIdentifierServiceUrl() + 'getGlobalMinMax/' + guid + '/';
        return this._requestService.get(url);
    }

    getIdentifier(guid: string): angular.IPromise<any> {
        const defered = this._$q.defer();
        this.getAll()
            .then((groupList) => {
                const groupDto = (_
                    .find(groupList,
                        (g: GroupDto) => _.some(g.group.identifierCollection,
                            (i: Identifier) => i.guid === guid)) as any) as GroupDto;
                let identifier: Identifier;
                if (groupDto) {
                    const group = groupDto.group;
                    identifier = _.find(group.identifierCollection, i => i.guid === guid);
                    (identifier as any).groupName = group.name;
                } else {
                    identifier = {
                        guid: constants.guidEmpty,
                        name: 'Unknown',
                        groupName: '',
                        variableTypeGuid: constants.guidEmpty,
                        unitGuid: constants.guidEmpty,
                        type: 1
                    } as any;
                    //defered.reject(`Could not find group with identifier ${guid}`);
                }
                defered.resolve(identifier);
            });
        return defered.promise;
    }

    getIdentifiers(guidList: string[]): angular.IPromise<Identifier[]> {
        const url = this.controllerUrl() + 'identifier/';
        return this._requestService.post(url, guidList);
    }

    createGroupFromTags(postObject: GroupFromTags): angular.IPromise<GroupDto> {
        const defered = this._$q.defer<GroupDto>();
        const url = this._requestService.getUrlV1() + 'group/tags/';
        this._requestService.cleanCache(this._requestService.getUrlV1());
        this._requestService.post(url, postObject, undefined, true)
            .then((dto: GroupDto) => {
                this._requestService.cleanCache(this._requestService.getUrlV1() + 'tagDefinition/');
                this._requestService.cleanCache(this._requestService.getUrlV1() + 'tagDefinition/getList');
                this._requestService.cleanCache(dto.url);
                this._requestService.cleanCache(this.controllerUrl());
                _.forEach(postObject.tags, (guid: string) => this.cleanTagCache(guid));
                this._requestService.cleanCacheAtV1('approvalStrategy/getGroups/');
                this._requestService.cleanCacheAtV1('group/steps/');
                this._requestService.cleanCacheAtV1(`group/${dto.group.guid}/`);
                defered.resolve(dto);
            })
            .catch((reason) => {
                defered.reject(reason);
            });
        return defered.promise;
    }

    addIdentifier(guid, identifier: Identifier): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'group/identifier/add/' + guid + '/';
        this._requestService.cleanCache(this._requestService.getUrlV1() + 'group/' + guid + '/');
        this._requestService.cleanCache(this._requestService.getUrlV1() + 'group/');
        this._requestService.cleanCache(this._requestService.getUrlV1() + 'viewModel/all');
        return this._requestService
            .post(url, identifier, undefined, true)
            .then((data) => {
                this._requestService.cleanCache(this._requestService.getUrlV1() + 'tagDefinition/');
                this._requestService.cleanCache(this._requestService.getUrlV1() + 'tagDefinition/getList');
                this._requestService.cleanCache(this._requestService.getUrlV1() + 'group/' + guid + '/');
                this._requestService.cleanCache(this._requestService.getUrlV1() + 'group/');
                this._requestService.cleanCache(this._requestService.getUrlV1() + 'viewModel/all');
                this.cleanCacheTemplate();
                this._requestService.cleanCacheAtV1('group/steps/');
                return data;
            });
    }

    updateIdentifiers(guid, identifiers: Identifier[]): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'group/identifiers/' + guid + '/';
        this._requestService.cleanCache(url);

        return this._requestService.post(url, identifiers, undefined, true)
            .then((data) => {
                this.cleanCacheTemplate();
                this._requestService.cleanCache(this._requestService.getUrlV1() + 'tagDefinition/');
                this._requestService.cleanCache(this._requestService.getUrlV1() + 'tagDefinition/getList');
                this._requestService.cleanCacheAtV1('group/steps/');
                this._requestService.cleanCache(this._requestService.getUrlV1() + 'group/' + guid + '/');
                this._requestService.cleanCache(this._requestService.getUrlV1() + 'group/');
                this._requestService.cleanCache(this._requestService.getUrlV1() + 'viewModel/all');
                this._requestService.cleanCacheAtV1('viewModel/guidList/');
                this._identifierVmService.cleanCache(identifiers.map(d => d.guid));
                this._requestService.cleanCacheAtV1('template/changes/all/');

                const tagIdentifiers = identifiers.filter(i => i.type === Enums.IdentifierType.Tag);
                if (tagIdentifiers && tagIdentifiers.length > 0) {
                    tagIdentifiers.forEach(i => this.cleanTagCache(i.tagDefinitionGuid));
                }
                return data;
            });
    }

    updateScript(guid: string, script: any): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'group/script/' + guid + '/';
        this._requestService.cleanCache(this._requestService.getUrlV1() + 'group/' + guid + '/');
        //this._requestService.cleanCache(this._requestService.getUrlV1() + 'template/changes/all');
        return this._requestService.post(url, script, undefined, true)
            .then((data) => {
                this.cleanCacheTemplate();
                this._requestService.cleanCacheAtV1('group/steps/');
                return data;
            });
    }

    getAllAsTreeOrdered(): angular.IPromise<Group[]> {
        const defered = this._$q.defer<Group[]>();
        this.getAll()
            .then(response => {
                const groups = response.map(d => d.group);
                const tree = this._groupTreeHelperService.getFlatChildren(groups, constants.guidEmpty);
                defered.resolve(tree);
            })
            .catch(reason => {
                this._loggerService.logError(reason);
                defered.reject(reason);
            });
        return defered.promise;
    }

    getAllAsTree(): angular.IPromise<TreeType[]> {
        const defered = this._$q.defer<TreeType[]>();
        this.getAll()
            .then(response => {
                try {
                    const groups = response.map(d => d.group);
                    const items = this.convertToTree(groups);
                    defered.resolve(items);
                } catch (e) {
                    this._loggerService.logError(e);
                    defered.reject(e);
                }
            })
            .catch((reason) => {
                defered.reject(reason);
            });
        return defered.promise;
    }

    convertToTree(groups: Group[]): TreeType[] {
        const tree = {
            name: 'root',
            guid: constants.guidEmpty,
            children: groups.filter(g => g.parentGUID === constants.guidEmpty) as TreeType[],
            isRoot: true
        };
        tree.children.forEach(c => {
            c.children = this.getChildren(groups, c.guid) as TreeType[];
        });
        const hierarchy = d3.layout.hierarchy<TreeType>();
        const items = hierarchy(tree as any);
        items.forEach(g => {
            g.isVisible = true;
            g.comment = g.shortComment;
        });
        return items;
    }

    private getChildren(groups: Group[], parentGuid: string): Group[] {
        const children = groups.filter(g => g.parentGUID === parentGuid);
        if (children.length > 0) {
            children.forEach((c: any) => {
                c.children = this.getChildren(groups, c.guid);
            });
        }
        return children;
    }

    private getConfigurationUrlForGroup(guid: string): string {
        return this._requestService.getUrlV1() + 'group/configuration/' + guid + '/';
    }

    getConfigurationForGroup(guid: string): angular.IPromise<IGroupConfiguration> {
        const url = this.getConfigurationUrlForGroup(guid);
        return this._requestService.get(url, undefined, true);
    }

    updateConfigurationForGroup(data: IGroupConfiguration): angular.IPromise<IGroupConfiguration> {
        const url = this._requestService.getUrlV1() + 'group/configuration/';
        const cleanUrl = this.getConfigurationUrlForGroup(data.guid);
        this._requestService.cleanCache(cleanUrl);
        return this._requestService.post(url, data, undefined, true)
            .then(res => {
                this.cleanCacheTemplate();
                return res;
            });
    }

    getReferenceStates(guid: string, force: boolean = false): angular.IPromise<ExtendedState[]> {
        if (this._dateRangeService.initialized()) {
            const defaultInterval = this._widgetIntervalHelperService.getCurrentInclusiveInterval();
            const url = this._requestService.getUrlV1() +
                'group/referenceStates/?groupGuid=' +
                guid +
                '&beginDate=' + defaultInterval.beginDate +
                '&endDate=' + defaultInterval.endDate;
            if (force) {
                this._requestService.cleanCache(url);
            }
            return this._requestService.get(url);
        } else {
            return this._$q.reject('Invalid dates');
        }
    }

    getActualStates(guid: string, force: boolean = false): angular.IPromise<any> {
        if (this._dateRangeService.initialized()) {
            const defaultInterval = this._widgetIntervalHelperService.getCurrentInclusiveInterval();
            const url = this._requestService.getUrlV1() +
                'group/actualStates/?groupGuid=' +
                guid +
                '&beginDate=' +
                defaultInterval.beginDate +
                '&endDate=' +
                defaultInterval.endDate;
            if (force) {
                this._requestService.cleanCache(url);
            }
            return this._requestService.get(url);
        } else {
            return this._$q.reject();
        }
    }

    testScript(groupGuid: string,
        script: string,
        date: string,
        scriptTestMode: Enums.ScriptTestMode,
        libraryCollection: string[]): angular.
        IPromise<any> {
        const url = this._requestService.getUrlV1() + 'group/testScript/?groupGuid=' + groupGuid + '&dateTime=' + date + '&mode=' + scriptTestMode;
        const data = {
            script: script,
            libraryCollection: libraryCollection
        };
        return this._requestService.post(url, data);
    }

    updateOccurenceMatrix(groupGuid: string,
        script: string,
        date: string,
        libraryCollection: string[],
        testMatrix: boolean): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'group/updateOccurenceMatrix/?groupGuid=' + groupGuid + '&dateTime=' + date + '&testMatrix=' + testMatrix;
        this._requestService.cleanAllCache();
        this._identifierVmService.cleanCache([]);
        this.cleanCacheTemplate();
        const data = {
            script: script,
            libraryCollection: libraryCollection
        };
        return this._requestService.post(url, data, undefined, true);
    }

    updateAllOccurenceMatrix(): angular.IPromise<{ [index: string]: string; }> {
        const url = this._requestService.getUrlV1() + 'group/updateAllOccurenceMatrix/';
        this._requestService.cleanAllCache();
        return this._requestService.post(url, undefined, true);
    }

    updateSomeOccurenceMatrix(guidList: string[]): angular.IPromise<{ [îndex: string]: string; }> {
        const url = this._requestService.getUrlV1() + 'group/updateSomeOccurenceMatrix/';
        this._requestService.cleanAllCache();
        return this._requestService.post(url, guidList, undefined, true);
    }

    isChildOrSubChild(guid: string, parentGuid: string): angular.IPromise<boolean> {
        const defered = this._$q.defer<boolean>();
        this.getAllAsTree()
            .then(tree => {
                const parent = _.find(tree, d => d.guid === parentGuid);
                let result = !!parent.children;
                if (result) {
                    result = this.isInHierarchy(guid, parent.children);
                }
                defered.resolve(result);
            })
            .catch(() => {
                defered.reject();
            });
        return defered.promise;
    }

    deleteIdentifier(groupGuid: string, identifierGuid: string): angular.IPromise<number> {
        const defered = this._$q.defer<number>();
        const url = this.getIdentifierServiceUrl() + 'delete/' + groupGuid + '/' + identifierGuid + '/';

        this.getIdentifier(identifierGuid)
            .then((identifier: Identifier) => {
                if (identifier.type === Enums.IdentifierType.Tag) {
                    this._requestService.cleanCache(this._requestService.getUrlV1() + 'tagDefinition/');
                    this.cleanTagCache(identifier.tagDefinitionGuid);
                }
                this._requestService.cleanCacheAtV1('group/steps/');
            })
            .finally(() => {
                this._requestService
                    .post(url)
                    .then(exitcode => {
                        this._requestService.cleanCache(this.controllerUrl() + groupGuid + '/');
                        this.cleanCacheTemplate();
                        defered.resolve(exitcode);
                    })
                    .catch(() => {
                        defered.reject();
                    });
            });

        return defered.promise;
    }

    duplicateIdentifier(groupGuid: string, identifierGuid: string, newIdentifierName: string, newTagDefinitionGuid: string): angular.IPromise<any> {
        const url = this.getIdentifierServiceUrl() + 'duplicate/' + groupGuid + '/' + identifierGuid + '/';
        this._requestService.cleanCacheAtV1('group/' + groupGuid + '/');
        this.cleanCacheTemplate();
        return this._requestService.post(url, { newIdentifierName: newIdentifierName, newTagDefinitionGuid: newTagDefinitionGuid }, undefined, true);
    }

    createFrom(data: CreateGroupFromGroupDto):
        angular.IPromise<GroupDto> {
        const url = this.controllerUrl() + 'createFrom/';
        return this._requestService.post(url, data, undefined, true)
            .then(result => {
                this.cleanCacheTemplate();
                this._requestService.cleanCache(this._requestService.getUrlV1() + 'tagDefinition/');
                this._requestService.cleanCacheAtV1('approvalStrategy/getGroups/');
                this._requestService.cleanCacheAtV1('group/steps/');
                return result;
            });
    }

    autofit(group: Group): angular.IPromise<any> {
        const defered = this._$q.defer();
        const url = this.controllerUrl() + 'autofit/';
        this._requestService.post(url, null, { groupGuid: group.guid })
            .then(data => {
                group.identifierCollection.map(i => 'identifier/rules/' + i.guid)
                    .forEach(url => this._requestService.cleanCacheAtV1(url));
                this._requestService.cleanCacheAtV1('template/changes/all/');
                defered.resolve(data);
            })
            .catch((reason) => defered.reject(reason));
        return defered.promise;
    }

    fitone(groupGuid: string, identifierGuid: string): angular.IPromise<any> {
        const defered = this._$q.defer();
        const url = this.controllerUrl() + 'fitone/';
        this._requestService.post(url, angular.toJson(identifierGuid), { groupGuid: groupGuid })
            .then(() => {
                this._requestService.cleanCacheAtV1('identifier/rules/' + identifierGuid + '/');
                this.cleanCacheTemplate();
                defered.resolve();
            })
            .catch(reason => defered.reject(reason));

        return defered.promise;
    }

    reconcile(groupGuid: string, dateTime: string): angular.IPromise<ReconciliationResults> {
        return this._requestService.postAtV1(
            'reconciliation/reconcile/',
            null,
            {
                groupGuid: groupGuid,
                timeStamp: dateTime
            }
        );
    }

    getPaths(guidList: string[]): angular.IPromise<{ guid: string; path: string }[]> {
        return this._requestService.cachedPostAtV1('group/getPaths/', guidList);
    }

    getForTypes(types: string[]): angular.IPromise<Group[]> {
        const defered = this._$q.defer<Group[]>();
        this.getAll()
            .then(data => {
                const result = data.map(d => d.group)
                    .filter(d => types.indexOf(d.type) >= 0);
                defered.resolve(result);
            })
            .catch(() => defered.reject());
        return defered.promise;

    }


    getPrecedencyTree(guid: string): angular.IPromise<any> {
        return this._requestService.getAtV1('group/getPrecedencyTree/' + guid + '/');
    }

    getDependencyTree(guid: string): angular.IPromise<any> {
        return this._requestService.getAtV1('group/getDependencyTree/' + guid + '/');
    }

    getGroupsUsers(): angular.IPromise<any[]> {
        const controllerAction = 'group/users/';
        this._requestService.cleanCacheAtV1(controllerAction);
        return this._requestService
            .getAtV1(controllerAction, undefined, true)
            .then((data: any[]) => {
                for (let i = 0; i < data.length; i++) {
                    if (data[i].users && data[i].users.length > 0) {
                        data[i].users = data[i].users.map(u => this.setState(u));
                    }
                }
                return data;
            });
    }

    getUsagesForAllGroups(): angular.IPromise<{ [guid: string]: boolean }> {
        const controllerAction = 'group/getUsagesAll/';
        this._requestService.cleanCacheAtV1(controllerAction);
        return this._requestService.getAtV1(controllerAction, undefined, true);
    }

    getUsagesForGroup(guid: string): angular.IPromise<ObjectUser[]> {
        this._requestService.cleanCacheAtV1('group/getUsages/' + guid + '/');
        return this._requestService.getAtV1('group/getUsages/' + guid + '/', undefined, true);
    }

    aggregateGuidForGroup(groups: Group[]): angular.IPromise<{ [guid: string]: string[] }> {
        const defered = this._$q.defer<{ [guid: string]: string[] }>();
        const ret = {};

        groups.forEach(g => {
            ret[g.guid] = [];
            GroupService.collectGuidFromGroup(ret[g.guid], g);

            // find all childs guid of each templated group
            // and count changes for them
            return this.getAll()
                .then(data => {
                    this._groupTreeHelperService
                        .getFlatChildren(data.map(g => g.group), g.guid)
                        .forEach(subgroup => GroupService.collectGuidFromGroup(ret[g.guid], subgroup));
                    defered.resolve(ret);
                })
                .catch(reason => defered.reject(reason));
        });

        return defered.promise;
    }

    getAggregateKey(guid: string, index: { [guid: string]: string[] }): string {
        for (const key of Object.keys(index)) {
            if (index.hasOwnProperty(key) && index[key].indexOf(guid) >= 0) {
                return key;
            }
        }
        return null;
    }

    cleanGroupCache() {
        this.cleanCacheTemplate();
    }

    updateLibraries(guid: string, libraries: string[], script: string, type: ScriptType): angular.IPromise<any> {
        return this._requestService.postAtV1('group/libraries/' + guid + '/', {
            LibraryCollection: libraries,
            script: script,
            type: type
        }, undefined, true)
            .then(() => {
                this._requestService.cleanCacheAtV1('group/');
                this._requestService.cleanCacheAtV1(`group/${guid}/`);
                this.cleanCacheTemplate();
            });
    }

    getSteps(): angular.IPromise<{ [groupGuid: string]: { [index: number]: boolean } }> {
        return this._requestService.getAtV1('group/steps/', undefined, true);
    }

    reorderIdentifiers(groupGuid: string, identifiersOrder: string[]): angular.IPromise<Identifier[]> {
        const url = `group/identifiers/reorder/${groupGuid}`;
        this._requestService.cleanCacheAtV1(url);
        return this._requestService.postAtV1(url, identifiersOrder, undefined, true);
    }

    getClosestByType(groupGuid: string, types: string[]): angular.IPromise<string> {
        const url = `group/getClosestByType/${groupGuid}`;
        return this._requestService.postAtV1(url, types);
    }

    upload(file: File): angular.IPromise<ImportResult> {
        const url = `${this.controllerUrl()}upload`;

        return this._uploadService
            .uploadFile<ImportResultDto<Group>>(url, file)
            .then(response => {
                //#27702: Clean all cache 'cos it's simplier
                this._requestService.cleanAllCache();
                return response;
            })
            .then(response => ({
                createdCount: response.created.length,
                failedCount: response.notUpdated.length,
                updatedCount: response.updated.length,
                result: response.result,
            }));
    }

    // compute special rules for UI to know if we could override usages and delete neitherless
    // introduced in #44507
    canDeleteGroup(guid: string, usages: ObjectUser[], groups: Dictionary<Group>): boolean {
        let canDelete = !(usages?.length > 0);

        if (!canDelete) {
            // specific cases if usages is validation page reference and we'll delete every group of that type
            // also cover the cases where this group is the last one of his kind
            const { keep: validationPageUsages, remove: leftOverUsages } = partition(usages, u => u.type === 'ValidationPageReference');
            // find group types that block validation page reference
            const types = validationPageUsages.map(u => u.name);
            // find all group attached to those types
            const groupsWithTypes = Object.entries(groups)
                .filter(kv => types.indexOf(kv[1].type) >= 0)
                .map(kv => kv[0]);
            // check if all groups of that kind have the group to be deleted as ancestor
            const areEveryToBeDeleted = _.every(groupsWithTypes, g => this.isAncestor(g, guid, groups));

            const hasNoUsageLeft = leftOverUsages.length === 0;
            canDelete = hasNoUsageLeft && areEveryToBeDeleted;
        }

        return canDelete;
    }

    private isAncestor(seek: string, ancestor: string, groups: Dictionary<Group>): boolean {
        let result = false;
        let parent = seek;

        while (!result && parent && parent != constants.guidEmpty) {
            result = parent === ancestor;
            parent = groups[parent]?.parentGUID;
        }

        return result;
    }

    private setState(user: { type: string; url: string; guid: string }): { type: string; url: string; guid: string } {
        switch (user.type) {
            case 'Group':
                user.url = this._$state.href('group.identifiers', { groupGuid: user.guid });
                break;
            case 'Dashboard':
                user.url = this._$state.href('dashboard', { dashboardName: user.guid });
                break;
            case 'Scheduling':
                user.url = this._$state.href('jobDefinitions');
                break;
            case 'Approval settings':
                user.url = this._$state.href('approvalConfiguration');
                break;
            case 'HistorianStorageConfiguration':
                user.url = this._$state.href('moreTools.exportConfiguration');
                break;
            case 'Trend':
                user.url = this._$state.href('trends');
                break;
            default:
                console.log(user.type + ' missing in getGroupsUsers()');
                break;
        }
        return user;
    }

    private isInHierarchy(guid: string, tree: any[]): boolean {
        let result = _.some(tree, g => g.guid === guid);
        if (!result) {
            tree.forEach(t => {
                if (t.children && t.children.length > 0) {
                    result = this.isInHierarchy(guid, t.children);
                }
            });
        }
        return result;
    }

    private static collectGuidFromGroup(collector: string[], group: Group): void {
        // TODO get all guid for others type ? like rules
        collector.push(String(group.identifierCollection.map(i => i.guid)));
        collector.push(group.guid);
    }

    private cleanCacheTemplate() {
        this._requestService.cleanCache(this.controllerUrl());
        this._requestService.cleanCacheAtV1('template/changes/all/');
        this._requestService.cleanCacheAtV1('template/changes/count/');
        this._requestService.cleanCacheAtV1('viewModel/all/');
        this._customPropertiesService.cleanCache();

    }

    private cleanTagCache(tagGuid: string) {
        this._requestService.cleanCache(this._requestService.getUrlV1() + `group/tag/${tagGuid}/`);
        this._requestService.cleanCache(this._requestService.getUrlV1() + `tagDefinition/${tagGuid}/`);
    }
}